<template>
  <div class="menu-editor" dir="rtl">
    <div class="me-head">
      <div>
        <h1 class="me-h1">فهرست‌ها</h1>
        <p class="me-desc">یک فهرست برای منوی دسکتاپ و منوی موبایل سایت. آیتم‌ها را با دستگیره بکشید؛ رها کردن روی وسط یک آیتم آن را زیرمنو می‌کند.</p>
      </div>
      <button type="button" class="btn btn-primary" :disabled="saving || loading" @click="saveAll">
        <span v-if="saving" class="spinner-border spinner-border-sm ml-1" />
        ذخیره فهرست
      </button>
    </div>

    <div class="me-grid">
      <aside class="me-side">
        <WpBox title="برگه‌ها" store-key="menu-pages">
          <div class="me-pick">
            <label v-for="p in sources.pages" :key="p.url" class="mn-check"><input v-model="pick.pages" type="checkbox" :value="p"> {{ p.title }}</label>
            <p v-if="!sources.pages.length" class="text-muted small mb-0">برگه منتشرشده‌ای نیست.</p>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary mt-2" :disabled="!pick.pages.length || adding" @click="addPicked('pages')">افزودن به فهرست</button>
        </WpBox>
        <WpBox title="دسته‌بندی‌ها" store-key="menu-cats">
          <input v-model="catSearch" class="form-control form-control-sm mb-2" placeholder="جستجو…">
          <div class="me-pick">
            <label v-for="c in filteredCats" :key="c.url" class="mn-check"><input v-model="pick.categories" type="checkbox" :value="c"> {{ c.title }}</label>
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary mt-2" :disabled="!pick.categories.length || adding" @click="addPicked('categories')">افزودن به فهرست</button>
        </WpBox>
        <WpBox title="پیوند دلخواه" store-key="menu-custom">
          <div class="form-group mb-2">
            <label>لینک</label>
            <CLinkSelector v-model="custom.url" placeholder="جستجوی محصول، دسته یا برند" />
            <input v-model="custom.url" dir="ltr" class="form-control form-control-sm mt-1" placeholder="https:// یا /path">
          </div>
          <div class="form-group mb-2">
            <label>متن پیوند</label>
            <input v-model="custom.title" class="form-control form-control-sm">
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!custom.title.trim() || adding" @click="addCustom">افزودن به فهرست</button>
        </WpBox>
      </aside>

      <section class="me-main">
        <div class="me-card">
          <div v-if="loading" class="me-skel"><div v-for="i in 5" :key="i" /></div>
          <template v-else>
            <ul class="mn-list" @dragover.prevent @drop.prevent.self="dropRoot">
              <MenuNode v-for="(m, i) in menus" :key="m.id" :item="m" :index="i" :siblings="menus" :depth="0" />
            </ul>
            <p v-if="!menus.length" class="text-muted text-center py-4 mb-0">فهرست خالی است؛ از ستون کناری آیتم اضافه کنید.</p>
          </template>
        </div>
        <div class="me-foot">
          <small class="text-muted">{{ fa(count) }} آیتم</small>
          <small v-if="dirty" class="text-warning mr-2">تغییرات ذخیره نشده دارید</small>
          <button type="button" class="btn btn-primary btn-sm mr-auto" :disabled="saving || loading" @click="saveAll">ذخیره فهرست</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "@/api/http";
import WpBox from "@/views/includes/WpBox.vue";
import MenuNode from "./MenuNode.vue";

const FA = '۰۱۲۳۴۵۶۷۸۹'
const MENU = 0 // single menu (desktop + mobile)

function prep(nodes) {
  return (nodes || []).map((n) => ({
    id: n.id, title: n.title || '', url: n.url || '', icon: n.icon || '', type: Number(n.type) || 1,
    target: n.target || '', _open: false, _dirty: false, children: prep(n.children)
  }))
}

export default {
  name: 'Menus',
  components: { WpBox, MenuNode },
  provide() { return { ed: this } },
  data() {
    return {
      menus: [], loading: true, saving: false, adding: false, dirty: false, dragId: 0, dragNode: null,
      sources: { pages: [], categories: [] }, pick: { pages: [], categories: [] }, catSearch: '',
      custom: { title: '', url: '' }
    }
  },
  computed: {
    count() { let n = 0; const walk = (l) => l.forEach((x) => { n++; walk(x.children) }); walk(this.menus); return n },
    filteredCats() {
      const q = this.catSearch.trim()
      return q ? this.sources.categories.filter((c) => c.title.includes(q)) : this.sources.categories
    }
  },
  watch: {
    '$route.params.menu_id'(v) { if (String(v) !== '0') this.$router.replace('/dashboard/display/menus/0') }
  },
  mounted() {
    if (String(this.$route.params.menu_id) !== '0') this.$router.replace('/dashboard/display/menus/0')
    this.load()
    this.loadSources()
    window.addEventListener('beforeunload', this.guard)
  },
  beforeDestroy() { window.removeEventListener('beforeunload', this.guard) },
  methods: {
    fa(n) { return String(n).replace(/\d/g, (d) => FA[d]) },
    guard(e) { if (this.dirty || this.anyDirty()) { e.preventDefault(); e.returnValue = '' } },
    load() {
      this.loading = true
      return axios.post('/api/admin/site/get_menu', { is_mobile: MENU }, { show_pros: false })
        .then((r) => { this.menus = prep(r.data.menus); this.dirty = false })
        .catch((e) => console.log(e))
        .finally(() => { this.loading = false })
    },
    loadSources() {
      axios.post('/api/admin/site/menu_sources', {}, { show_pros: false })
        .then((r) => { if (r.data && !r.data.error) this.sources = { pages: r.data.pages || [], categories: r.data.categories || [] } })
        .catch(() => {})
    },
    // ---- tree helpers
    locate(node, list = this.menus, parent = null) {
      for (let i = 0; i < list.length; i++) {
        if (list[i] === node) return { list, index: i, parent }
        const f = this.locate(node, list[i].children, list[i])
        if (f) return f
      }
      return null
    },
    contains(a, b) { return a === b || a.children.some((c) => this.contains(c, b)) },
    detach(node) { const l = this.locate(node); if (l) l.list.splice(l.index, 1); return l },
    move(node, d) {
      const l = this.locate(node); const j = l.index + d
      if (j < 0 || j >= l.list.length) return
      l.list.splice(l.index, 1); l.list.splice(j, 0, node); this.dirty = true
    },
    indent(node) {
      const l = this.locate(node); if (!l || l.index === 0) return
      const prev = l.list[l.index - 1]
      l.list.splice(l.index, 1); prev.children.push(node); this.dirty = true
    },
    outdent(node) {
      const l = this.locate(node); if (!l || !l.parent) return
      const p = this.locate(l.parent)
      l.list.splice(l.index, 1); p.list.splice(p.index + 1, 0, node); this.dirty = true
    },
    dragStart(node) { this.dragNode = node; this.dragId = node.id },
    dragEnd() { this.dragNode = null; this.dragId = 0 },
    dropAt(target, where) {
      const n = this.dragNode; this.dragEnd()
      if (!n || this.contains(n, target)) return
      this.detach(n)
      if (where === 'inside') { target.children.push(n) } else {
        const t = this.locate(target)
        t.list.splice(where === 'before' ? t.index : t.index + 1, 0, n)
      }
      this.dirty = true
    },
    dropInto(parent) {
      const n = this.dragNode; this.dragEnd()
      if (!n || this.contains(n, parent)) return
      this.detach(n); parent.children.push(n); this.dirty = true
    },
    dropRoot() {
      const n = this.dragNode; this.dragEnd()
      if (!n) return
      this.detach(n); this.menus.push(n); this.dirty = true
    },
    anyDirty() { let d = false; const w = (l) => l.forEach((x) => { if (x._dirty) d = true; w(x.children) }); w(this.menus); return d },
    // ---- server
    flat() {
      const out = []
      const walk = (l, pid) => l.forEach((x, i) => { out.push({ id: x.id, parent_id: pid, order_no: i, node: x }); walk(x.children, x.id) })
      walk(this.menus, 0)
      return out
    },
    async saveAll() {
      if (this.saving) return
      const rows = this.flat()
      const bad = rows.find((r) => !r.node.title.trim())
      if (bad) { bad.node._open = true; return this.$root.modal_component.show_danger_modal('خطا', 'عنوان آیتم‌ها نباید خالی باشد') }
      this.saving = true
      try {
        for (const r of rows.filter((x) => x.node._dirty)) {
          const n = r.node
          const res = await axios.post('/api/admin/site/update_menu', {
            row_id: n.id, title: n.title, url: n.url, icon: n.icon, type: n.type, target: n.target,
            parent_id: r.parent_id, order_no: r.order_no, is_mobile: MENU
          }, { show_pros: false })
          if (res.data && res.data.error) throw new Error(res.data.msg || 'خطا در ذخیره «' + n.title + '»')
          n._dirty = false
        }
        const res = await axios.post('/api/admin/site/save_menu_order', {
          is_mobile: MENU, items: rows.map((r) => ({ id: r.id, parent_id: r.parent_id }))
        }, { show_pros: false })
        if (res.data && res.data.error) throw new Error(res.data.msg || 'خطا در ذخیره ترتیب')
        this.dirty = false
        this.$root.modal_component.show_success_modal('تایید', 'فهرست ذخیره شد')
      } catch (e) {
        this.$root.modal_component.show_danger_modal('خطا', e.message || 'خطا در ذخیره')
      } finally {
        this.saving = false
      }
    },
    async addItems(list) {
      if (this.dirty || this.anyDirty()) await this.saveAll()
      this.adding = true
      try {
        let order = this.menus.length
        for (const it of list) {
          await axios.post('/api/admin/site/add_menu', {
            title: it.title, url: it.url, icon: '', type: 1, target: '', parent_id: 0, order_no: order++, is_mobile: MENU
          }, { show_pros: false })
        }
        await this.load()
      } finally {
        this.adding = false
      }
    },
    addPicked(kind) { const l = this.pick[kind].slice(); this.pick[kind] = []; return this.addItems(l) },
    addCustom() {
      const it = { title: this.custom.title.trim(), url: this.custom.url.trim() || '#' }
      this.custom = { title: '', url: '' }
      return this.addItems([it])
    },
    remove(node) {
      const sub = node.children.length ? '\nزیرمنوهای آن یک سطح بالا می‌آیند.' : ''
      if (!window.confirm('آیتم «' + node.title + '» حذف شود؟' + sub)) return
      axios.post('/api/admin/site/delete_menu', { row_id: node.id }, { show_pros: false }).then((r) => {
        if (r.data && r.data.error) return this.$root.modal_component.show_danger_modal('خطا', r.data.msg)
        const l = this.detach(node)
        if (l) l.list.splice(l.index, 0, ...node.children)
      }).catch((e) => console.log(e))
    }
  }
}
</script>

<style>
.menu-editor .me-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.menu-editor .me-h1 { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.menu-editor .me-desc { color: #50575e; margin: 0; font-size: 13px; }
.menu-editor .me-grid { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 20px; align-items: start; }
.menu-editor .me-side { position: sticky; top: 16px; }
.menu-editor .me-card { background: #fff; border: 1px solid #dcdcde; border-radius: 6px; padding: 16px; }
.menu-editor .me-foot { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
.menu-editor .me-pick { max-height: 240px; overflow: auto; border: 1px solid #eee; border-radius: 4px; padding: 8px; }
.menu-editor .mn-check { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; cursor: pointer; font-size: 13px; }
.menu-editor .me-skel div { height: 44px; border-radius: 4px; background: #f0f0f1; margin-bottom: 8px; animation: me-p 1.2s infinite; }
@keyframes me-p { 50% { opacity: .5 } }
.mn-list { list-style: none; margin: 0; padding: 0; min-height: 6px; }
.mn-children { padding-right: 28px; }
.mn { margin-bottom: 6px; }
.mn--drag > .mn-card { opacity: .45; }
.mn-card { display: flex; align-items: center; gap: 8px; background: #f6f7f7; border: 1px solid #dcdcde; border-radius: 4px; padding: 8px 10px; transition: box-shadow .1s; }
.mn-card:hover { border-color: #8c8f94; }
.mn-drop-before { box-shadow: 0 -3px 0 #2271b1; }
.mn-drop-after { box-shadow: 0 3px 0 #2271b1; }
.mn-drop-inside { box-shadow: inset 0 0 0 2px #2271b1; background: #f0f6fc; }
.mn-handle { cursor: grab; color: #8c8f94; font-weight: 700; letter-spacing: -2px; user-select: none; padding: 0 4px; }
.mn-title { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; background: none; border: 0; text-align: right; padding: 0; color: #1d2327; }
.mn-title b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.mn-type, .mn-sub { color: #646970; font-size: 11px; }
.mn-tools { display: flex; gap: 2px; }
.mn-tools button { background: none; border: 1px solid transparent; border-radius: 3px; color: #50575e; width: 26px; height: 26px; line-height: 1; padding: 0; }
.mn-tools button:hover:not(:disabled) { border-color: #c3c4c7; background: #fff; }
.mn-tools button:disabled { opacity: .3; }
.mn-edit { border: 1px solid #dcdcde; border-top: 0; border-radius: 0 0 4px 4px; background: #fff; padding: 12px; margin-top: -2px; }
.mn-edit label { font-size: 12px; color: #50575e; }
.mn-edit-foot { display: flex; align-items: center; gap: 8px; font-size: 13px; }
@media (max-width: 991px) {
  .menu-editor .me-grid { grid-template-columns: 1fr; }
  .menu-editor .me-side { position: static; order: 2; }
  .menu-editor .me-main { order: 1; }
  .mn-children { padding-right: 16px; }
}
</style>
